import { useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Vehicle } from 'types/vehicle';
import { useAddTransferNoteMutation } from 'services/api/transfer-report';
import api from 'services/api';
import { getUser } from 'utils/auth';
import { selectTabFilters } from 'services/salesHistorySlice/salesHistorySlice';
import toast from 'react-hot-toast';
import { ProgressStepper, VehicleNotes } from 'components/vehicle-history';
import type { NoteItem } from 'components/vehicle-history';

export type { NoteItem };

export interface TransferRecord {
  id?: number;
  notes?: string | null;
  date_sold?: string | null;
  date_title_received?: string | null;
  date_title_sent_out?: string | null;
  title_sent_to?: string | null;
  date_pmt_dollar_received?: string | null;
  usps_fed_ex_tracking?: string | null;
  x_title_inn?: string | null;
  date_seller_was_paid?: string | null;
  date_cleared?: string | null;
  status?: string;
  has_documents?: boolean;
  notesList?: NoteItem[];
}

interface VehicleTitleBlockProps {
  vehicle: Vehicle & { transferRecord?: TransferRecord };
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const PurchasedBadge = styled.span`
  background: #e0e0e0;
  color: #000;
  font-size: 16px;
  font-weight: 700;
  border-radius: 20px;
  padding: 6px 14px;
`;

const TitleNotInBadge = styled.span`
  background: #e0e0e0;
  color: #000;
  font-size: 16px;
  font-weight: 700;
  border-radius: 20px;
  padding: 6px 14px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const TitleLabel = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #000;
`;

const TitleLink = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: #005a96;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return '';
  const m = moment(dateStr);
  return m.isValid() ? m.format('MM/DD/YYYY') : '';
};

const VehicleTitleBlock = ({ vehicle }: VehicleTitleBlockProps) => {
  const tr = vehicle.transferRecord;
  const dispatch = useDispatch();
  const { pages, perPage, tab } = useSelector((state: any) => state.salesHistory);
  const filters = useSelector((state: any) => selectTabFilters(state, tab));
  const page = pages[tab] || 1;
  const TAB_FILTERS: Record<string, Record<string, string>> = {
    sold: { status: 'awarded' },
    waiting_on_title: { title_status: 'waiting_on_title' }
  };
  const tabFilters = TAB_FILTERS[tab] ?? {};
  const [addNote, { isLoading }] = useAddTransferNoteMutation();

  const currentUser = getUser();

  const steps = useMemo(() => [
    { label: 'Sell date', active: true, date: formatDate(tr?.date_sold) },
    { label: 'Title Received', active: !!tr?.date_title_received, date: formatDate(tr?.date_title_received) },
    { label: 'Payment Sent', active: !!tr?.date_seller_was_paid, date: formatDate(tr?.date_seller_was_paid) },
    { label: 'Payment Cleared', active: !!tr?.date_cleared, date: formatDate(tr?.date_cleared) }
  ], [tr?.date_sold, tr?.date_title_received, tr?.date_seller_was_paid, tr?.date_cleared]);

  const purchasedDate = formatDate(tr?.date_sold);
  const notesList = tr?.notesList ?? [];

  const titleInReceived = !!tr?.date_title_received || tr?.x_title_inn === 'X';
  const titleNotInDays = (() => {
    if (titleInReceived || !tr?.date_sold) return null;
    const days = moment().diff(moment.utc(tr.date_sold), 'days');
    return days;
  })();

  const handleSend = async (message: string) => {
    const newNote = await addNote({
      id: tr!.id!,
      payload: { message, visible_to_seller: true, visible_to_buyer: false }
    }).unwrap();

    const userName =
      currentUser?.name || `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Me';

    const noteItem: NoteItem = {
      id: newNote.id,
      message,
      created_at: newNote.created_at || newNote.timestamp || new Date().toISOString(),
      sender: { id: currentUser?.id, role: 'seller', name: userName }
    };

    dispatch(
      (api as any).util.updateQueryData('getSalesHistory', { limit: perPage, offset: page, ...filters, ...tabFilters }, (draft: any) => {
        const entity = draft.rows.entities[vehicle.id];
        if (entity?.transferRecord) {
          entity.transferRecord.notesList = [...(entity.transferRecord.notesList ?? []), noteItem];
        }
      })
    );
  };

  const handleSendWithToast = async (message: string) => {
    try {
      await handleSend(message);
    } catch (e) {
      toast.error('Failed to send note. Please try again.');
      throw e;
    }
  };

  return (
    <Wrapper>
      <TopRow>
        {purchasedDate && <PurchasedBadge>Sold: {purchasedDate}</PurchasedBadge>}
        {titleNotInDays !== null && <TitleNotInBadge>Title not in: {titleNotInDays} Days</TitleNotInBadge>}
      </TopRow>
      {/* <TitleRow>
        <TitleLabel>Vehicle Title</TitleLabel>
        {tr?.has_documents && tr?.id ? (
          <TitleLink onClick={() => window.open(`/seller/transfer-report/${tr.id}/document`, '_blank')}>
            See title copy
          </TitleLink>
        ) : (
          <span style={{ fontSize: 16, fontWeight: 400, color: '#999' }}>No copy available yet.</span>
        )}
      </TitleRow> */}
      <ProgressStepper steps={steps} notes={tr?.notes ?? undefined} />
      <VehicleNotes
        notesList={notesList}
        currentRole="seller"
        onSend={handleSendWithToast}
        isSending={isLoading}
        canSend={!!tr?.id}
      />
    </Wrapper>
  );
};

export default VehicleTitleBlock;
